// Asian Handicap Value Dashboard (v3 teardown).
//
// Lives at /asian-handicap. Daily slate → fixture analyser → performance
// tracker → confidence calibration, all under the v3 Quiet Precision
// chrome (hairline sections, mono accents, flat tables, single indigo).

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Target } from "lucide-react";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import TopNav from "../components/TopNav";
import DailyValueSlateCard from "../components/DailyValueSlateCard";
import {
  V3Page, V3Container, V3Header, V3Eyebrow, V3Section, V3Label,
  V3Tag, V3Num, V3Segment, V3Button,
} from "../components/v3";

const TIER_TONES = {
  "A+":    "emerald",
  "A":     "emerald",
  "B+":    "amber",
  "B":     "amber",
  "C":     "mute",
  "NO BET":"rose",
};

const MARGIN_LABELS = {
  lose_by_2_plus: "Lose 2+",
  lose_by_1: "Lose 1",
  draw: "Draw",
  win_by_1: "Win 1",
  win_by_2: "Win 2",
  win_by_3_plus: "Win 3+",
};

export default function AsianHandicapPage() {
  return (
    <V3Page testid="ah-page">
      <TopNav />
      <V3Container>
        <V3Header
          testid="ah-header"
          eyebrow={
            <V3Eyebrow icon={Target} backTo="/" backLabel="Home">
              xGD · Monte Carlo · Edge · ROI
            </V3Eyebrow>
          }
          title="Asian handicap."
          titleAccent="Value engine — every line, every edge."
          subtitle="Bivariate-Poisson + 50 000 Monte Carlo paths over every standard AH line. Daily slate above; per-fixture analyser, performance tracker and confidence calibration below."
        />

        <V3Section label="Daily slate" testid="ah-slate-section">
          <DailyValueSlateCard variant="ah" testidPrefix="ah-slate" />
        </V3Section>

        <AnalyseSection />
        <PerformanceSection />
        <CalibrationSection />
      </V3Container>
    </V3Page>
  );
}


// ── Analyse one fixture ───────────────────────────────────────────────

function AnalyseSection() {
  const [form, setForm] = useState({
    home_team: "Manchester City",
    away_team: "Sheffield United",
    league: "Premier League",
    lambda_home: "2.4",
    lambda_away: "0.6",
    rho: "0.10",
  });
  const [marketLines, setMarketLines] = useState([
    { line: "-1.0", home_odds: "1.50", away_odds: "2.65" },
    { line: "0",    home_odds: "1.20", away_odds: "4.50" },
    { line: "1.0",  home_odds: "1.05", away_odds: "8.50" },
  ]);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setLine  = (i, k, v) => setMarketLines((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addLine  = () => setMarketLines((rows) => [...rows, { line: "0.5", home_odds: "1.90", away_odds: "1.95" }]);
  const removeLine = (i) => setMarketLines((rows) => rows.filter((_, j) => j !== i));

  async function runAnalysis() {
    setLoading(true);
    setError(null);
    setAnalysis(null);
    try {
      const body = {
        home_team: form.home_team,
        away_team: form.away_team,
        league: form.league,
        lambda_home: parseFloat(form.lambda_home),
        lambda_away: parseFloat(form.lambda_away),
        rho: parseFloat(form.rho || "0.10"),
        market_lines: Object.fromEntries(
          marketLines
            .filter((r) => r.home_odds && r.away_odds)
            .map((r) => [String(parseFloat(r.line)),
              { home_odds: parseFloat(r.home_odds), away_odds: parseFloat(r.away_odds) }]),
        ),
        mc_iterations: 5000,
      };
      const res = await axios.post(`${API}/asian-handicap/analyze`, body);
      setAnalysis(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Analyse failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <V3Section label="Analyse fixture" testid="ah-analyse-section">
      <p className="text-[13px] mb-6 max-w-2xl" style={{ color: "var(--v3-text-3)" }}>
        Provide the expected goal rates (λ) and the bookmaker's AH lines.
        We'll run 5 000 Monte Carlo paths + bivariate-Poisson analysis to
        score every line and flag mispriced ones.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Field label="Home team"                    testId="ah-home-team"    value={form.home_team}    onChange={(v) => setField("home_team", v)} />
        <Field label="Away team"                    testId="ah-away-team"    value={form.away_team}    onChange={(v) => setField("away_team", v)} />
        <Field label="League"                       testId="ah-league"       value={form.league}       onChange={(v) => setField("league", v)} />
        <Field label="λ home (expected home goals)" testId="ah-lambda-home"  value={form.lambda_home}  onChange={(v) => setField("lambda_home", v)} type="number" />
        <Field label="λ away (expected away goals)" testId="ah-lambda-away"  value={form.lambda_away}  onChange={(v) => setField("lambda_away", v)} type="number" />
        <Field label="ρ (goal correlation, 0–0.6)"  testId="ah-rho"          value={form.rho}          onChange={(v) => setField("rho", v)} type="number" />
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <V3Label>Bookmaker AH lines</V3Label>
          <button
            onClick={addLine}
            data-testid="ah-add-line"
            className="text-[11px] uppercase hover:opacity-80 transition-opacity"
            style={{
              color: "var(--v3-indigo-fg)",
              fontFamily: "var(--v3-font-mono)",
              letterSpacing: "0.14em",
            }}
          >
            + Add line
          </button>
        </div>
        <div style={{ borderTop: "1px solid var(--v3-line)" }}>
          {marketLines.map((r, i) => (
            <div
              key={i}
              className="flex gap-2 items-center py-2 flex-wrap"
              style={{ borderBottom: "1px solid var(--v3-line)" }}
              data-testid={`ah-market-row-${i}`}
            >
              <MonoInput value={r.line}      onChange={(v) => setLine(i, "line", v)}      placeholder="Line"       width={88}  testId={`ah-market-line-${i}`} />
              <MonoInput value={r.home_odds} onChange={(v) => setLine(i, "home_odds", v)} placeholder="Home odds"  width={112} testId={`ah-market-home-${i}`} type="number" />
              <MonoInput value={r.away_odds} onChange={(v) => setLine(i, "away_odds", v)} placeholder="Away odds"  width={112} testId={`ah-market-away-${i}`} type="number" />
              <button
                onClick={() => removeLine(i)}
                data-testid={`ah-market-remove-${i}`}
                className="text-[12px] hover:opacity-80 ml-auto"
                style={{ color: "var(--v3-rose, #fda4af)", fontFamily: "var(--v3-font-mono)" }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <V3Button
          onClick={runAnalysis}
          disabled={loading}
          data-testid="ah-run-analysis"
        >
          {loading ? "Analysing…" : "Run analysis"}
        </V3Button>
        {error && (
          <span
            className="text-[12px]"
            style={{ color: "var(--v3-rose, #fda4af)", fontFamily: "var(--v3-font-mono)" }}
            data-testid="ah-error"
          >
            {error}
          </span>
        )}
      </div>

      {analysis && <AnalysisResult data={analysis} />}
    </V3Section>
  );
}


function AnalysisResult({ data }) {
  const tierTone = TIER_TONES[data.risk_tier] || "mute";
  const sortedLines = useMemo(
    () => [...data.line_evaluations].sort((a, b) => {
      const ea = a.edge_pct ?? -999, eb = b.edge_pct ?? -999;
      return eb - ea;
    }),
    [data.line_evaluations],
  );

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-0" style={{ borderTop: "1px solid var(--v3-line)" }} data-testid="ah-analysis-result">
      <div
        className="p-5"
        style={{
          border: "1px solid var(--v3-line)",
          background: "var(--v3-surface)",
          borderRight: "none",
          borderTop: "none",
        }}
      >
        <V3Label>Verdict</V3Label>
        <div className="mt-3 flex items-center gap-3">
          <V3Tag tone={tierTone} testid="ah-verdict-tier">{data.risk_tier}</V3Tag>
          <V3Num className="text-xl">conf {data.confidence_pct}%</V3Num>
        </div>
        {data.best_bet ? (
          <>
            <div className="mt-4 text-[14px]" style={{ fontWeight: 500 }}>
              {data.best_bet.side === "home" ? data.home_team : data.away_team}
              {" "}AH {data.best_bet.line >= 0 ? `+${data.best_bet.line}` : data.best_bet.line}
            </div>
            <div className="mt-2 text-3xl" data-testid="ah-edge" style={{ color: "var(--v3-indigo-fg)", fontFamily: "var(--v3-font-mono)", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
              +{data.best_bet.edge_pct.toFixed(2)}%
            </div>
            <div className="mt-3 text-[11px]" style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}>
              Market {data.best_bet.market_odds} · Fair {data.best_bet.fair_odds}
              <br />
              Cover {(data.best_bet.cover_probability * 100).toFixed(1)}%
            </div>
          </>
        ) : (
          <div className="mt-4 text-[13px]" style={{ color: "var(--v3-text-3)" }} data-testid="ah-no-bet">
            {data.no_bet_reason || "No value detected."}
          </div>
        )}
      </div>

      <div
        className="p-5"
        style={{
          border: "1px solid var(--v3-line)",
          background: "var(--v3-surface)",
          borderRight: "none",
          borderTop: "none",
        }}
      >
        <V3Label>Expected goals</V3Label>
        <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div className="text-right">
            <V3Num className="text-2xl">{data.lambda_home}</V3Num>
            <div className="text-[11px]" style={{ color: "var(--v3-text-3)" }}>{data.home_team}</div>
          </div>
          <span className="text-[10px] uppercase" style={{ color: "var(--v3-text-4)", fontFamily: "var(--v3-font-mono)", letterSpacing: "0.14em" }}>vs</span>
          <div>
            <V3Num className="text-2xl">{data.lambda_away}</V3Num>
            <div className="text-[11px]" style={{ color: "var(--v3-text-3)" }}>{data.away_team}</div>
          </div>
        </div>
        <KVList
          rows={[
            ["xGD",            <V3Num>{data.expected_goal_difference}</V3Num>, "ah-xgd"],
            ["MC xGD (50k)",   <V3Num>{data.monte_carlo_xgd}</V3Num>],
            ["Fair handicap",  <V3Num>{data.fair_handicap_line >= 0 ? `+${data.fair_handicap_line}` : data.fair_handicap_line}</V3Num>],
            ["Most likely",    <span><V3Num>{MARGIN_LABELS[data.most_likely_margin]}</V3Num> <span style={{ color: "var(--v3-text-3)" }}>({data.most_likely_margin_pct}%)</span></span>],
          ]}
        />
      </div>

      <div
        className="p-5"
        style={{
          border: "1px solid var(--v3-line)",
          background: "var(--v3-surface)",
          borderTop: "none",
        }}
      >
        <V3Label>Margin distribution</V3Label>
        <div className="mt-3 space-y-2">
          {Object.entries(data.margin_probabilities).map(([k, pct]) => (
            <Bar key={k} label={MARGIN_LABELS[k] || k} pct={pct} />
          ))}
        </div>
      </div>

      <div
        className="lg:col-span-3 p-5 overflow-x-auto"
        style={{ border: "1px solid var(--v3-line)", background: "var(--v3-surface)", borderTop: "none" }}
      >
        <V3Label>Every line · sorted by edge</V3Label>
        <table className="w-full text-[13px] mt-3" style={{ fontVariantNumeric: "tabular-nums" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--v3-line)" }}>
              {["Line", "Side", "Cover", "Fair", "Market", "Edge", "Push"].map((h) => (
                <th key={h} className="py-2 text-left text-[10px] uppercase pr-3" style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)", letterSpacing: "0.14em", fontWeight: 500 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedLines.map((e, i) => (
              <tr key={i} style={{ borderBottom: "1px solid var(--v3-line)" }} data-testid={`ah-eval-row-${i}`}>
                <td className="py-1.5 pr-3" style={{ fontFamily: "var(--v3-font-mono)" }}>{e.line >= 0 ? `+${e.line}` : e.line}</td>
                <td className="pr-3 capitalize" style={{ color: "var(--v3-text-2)" }}>{e.side}</td>
                <td className="pr-3">{(e.cover_probability * 100).toFixed(1)}%</td>
                <td className="pr-3">{e.fair_odds}</td>
                <td className="pr-3">{e.market_odds ?? "—"}</td>
                <td
                  className="pr-3"
                  style={{
                    color:
                      e.edge_pct == null ? "var(--v3-text-4)" :
                      e.edge_pct >= 3   ? "var(--v3-indigo-fg)" :
                      e.edge_pct >= 0   ? "var(--v3-text)" : "var(--v3-rose, #fda4af)",
                    fontWeight: e.edge_pct != null && e.edge_pct >= 3 ? 500 : 400,
                  }}
                >
                  {e.edge_pct == null ? "—" : `${e.edge_pct >= 0 ? "+" : ""}${e.edge_pct.toFixed(2)}%`}
                </td>
                <td className="pr-3">{e.push_probability ? `${(e.push_probability * 100).toFixed(1)}%` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Performance ───────────────────────────────────────────────────────

function PerformanceSection() {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [range, setRange] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/asian-handicap/performance?range_days=${range}`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (!cancel) setData(res.data);
      } catch (e) {
        if (!cancel) setError(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancel = true; };
  }, [user, token, range]);

  if (!user) {
    return (
      <V3Section label="Performance tracking" testid="ah-perf-signed-out">
        <p className="text-[13px]" style={{ color: "var(--v3-text-3)" }}>
          Sign in to record AH value bets and track lifetime ROI.
        </p>
      </V3Section>
    );
  }

  return (
    <V3Section
      label="Performance tracking"
      right={
        <V3Segment
          value={range}
          onChange={setRange}
          testidPrefix="ah-range"
          options={[
            { value: 0,  label: "Lifetime", testid: "ah-range-0"  },
            { value: 7,  label: "7d",       testid: "ah-range-7"  },
            { value: 30, label: "30d",      testid: "ah-range-30" },
            { value: 90, label: "90d",      testid: "ah-range-90" },
          ]}
        />
      }
    >
      {error && (
        <div className="text-[13px]" style={{ color: "var(--v3-rose, #fda4af)" }}>{error}</div>
      )}
      {!data ? (
        <div className="text-[13px]" style={{ color: "var(--v3-text-4)", fontFamily: "var(--v3-font-mono)" }} data-testid="ah-perf-loading">Loading…</div>
      ) : data.summary.total_settled_bets === 0 ? (
        <div
          className="p-5 text-[13px]"
          style={{
            border: "1px dashed var(--v3-line-2)",
            background: "var(--v3-surface)",
            color: "var(--v3-text-3)",
          }}
          data-testid="ah-perf-empty"
        >
          No settled AH bets yet for this range. Record value bets from the analyse panel and settle them to start tracking your edge.
        </div>
      ) : (
        <>
          <SummaryGrid s={data.summary} />
          <RollupTable title="By league"    rows={data.by_league} keyName="league" />
          <RollupTable title="By risk tier" rows={data.by_tier}   keyName="tier"  />
        </>
      )}
    </V3Section>
  );
}

function SummaryGrid({ s }) {
  const cards = [
    { label: "Total bets", value: s.total_settled_bets,                                           testId: "ah-sum-bets" },
    { label: "Hit rate",   value: `${s.hit_rate_pct.toFixed(1)}%`,                                testId: "ah-sum-hit"  },
    { label: "ROI",        value: `${s.roi_pct >= 0 ? "+" : ""}${s.roi_pct.toFixed(2)}%`,         testId: "ah-sum-roi", tone: s.roi_pct >= 0 ? "var(--v3-indigo-fg)" : "var(--v3-rose, #fda4af)" },
    { label: "P&L",        value: `${s.gross_pnl >= 0 ? "+" : ""}${s.gross_pnl.toFixed(2)}`,      testId: "ah-sum-pnl", tone: s.gross_pnl >= 0 ? "var(--v3-indigo-fg)" : "var(--v3-rose, #fda4af)" },
    { label: "Avg CLV",    value: s.avg_clv_pct == null ? "—" : `${s.avg_clv_pct >= 0 ? "+" : ""}${s.avg_clv_pct.toFixed(2)}%`, testId: "ah-sum-clv" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-0" style={{ borderTop: "1px solid var(--v3-line)" }}>
      {cards.map((c, i) => (
        <div
          key={c.label}
          className="p-4"
          style={{
            borderBottom: "1px solid var(--v3-line)",
            borderRight: i < cards.length - 1 ? "1px solid var(--v3-line)" : "none",
            background: "var(--v3-surface)",
          }}
          data-testid={c.testId}
        >
          <V3Label>{c.label}</V3Label>
          <div
            className="mt-2 text-xl"
            style={{
              color: c.tone || "var(--v3-text)",
              fontFamily: "var(--v3-font-mono)",
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "-0.02em",
              fontWeight: 500,
            }}
          >
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function RollupTable({ title, rows, keyName }) {
  const items = Object.entries(rows || {});
  if (!items.length) return null;
  return (
    <div className="mt-6">
      <V3Label>{title}</V3Label>
      <div className="mt-3 overflow-x-auto" style={{ border: "1px solid var(--v3-line)", background: "var(--v3-surface)" }}>
        <table className="w-full text-[13px]" style={{ fontVariantNumeric: "tabular-nums" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--v3-line)" }}>
              {[(keyName === "league" ? "League" : "Tier"), "Bets", "Hit rate", "ROI", "P&L"].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-[10px] uppercase" style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)", letterSpacing: "0.14em", fontWeight: 500 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.sort((a, b) => b[1].bets - a[1].bets).map(([name, row]) => (
              <tr key={name} style={{ borderBottom: "1px solid var(--v3-line)" }} data-testid={`ah-${keyName}-row-${name}`}>
                <td className="px-3 py-2" style={{ fontWeight: 500 }}>{name}</td>
                <td className="px-3 py-2">{row.bets}</td>
                <td className="px-3 py-2">{row.hit_rate_pct.toFixed(1)}%</td>
                <td className="px-3 py-2" style={{ color: row.roi_pct >= 0 ? "var(--v3-indigo-fg)" : "var(--v3-rose, #fda4af)" }}>
                  {row.roi_pct >= 0 ? "+" : ""}{row.roi_pct.toFixed(2)}%
                </td>
                <td className="px-3 py-2" style={{ color: row.pnl >= 0 ? "var(--v3-indigo-fg)" : "var(--v3-rose, #fda4af)" }}>
                  {row.pnl >= 0 ? "+" : ""}{row.pnl.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Calibration ───────────────────────────────────────────────────────

function CalibrationSection() {
  const { user, token } = useAuth();
  const [bins, setBins] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/asian-handicap/calibration`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (!cancel) setBins(res.data.bins || {});
      } catch (_e) {}
    })();
    return () => { cancel = true; };
  }, [user, token]);

  if (!user) return null;
  if (!bins || !Object.keys(bins).length) {
    return (
      <V3Section label="Confidence calibration" testid="ah-calib-empty">
        <p className="text-[13px] max-w-2xl" style={{ color: "var(--v3-text-3)" }}>
          Once you have ≥10 settled bets we'll plot cover-probability buckets vs
          actual hit rate so you can see whether the engine's confidence is well
          calibrated.
        </p>
      </V3Section>
    );
  }

  return (
    <V3Section label="Confidence calibration">
      <p className="text-[13px] mb-4 max-w-2xl" style={{ color: "var(--v3-text-3)" }}>
        Each row groups bets by predicted cover probability (10-pct buckets)
        and compares to realised hit rate. Calibration error should hover
        near 0%.
      </p>
      <div className="overflow-x-auto" style={{ border: "1px solid var(--v3-line)", background: "var(--v3-surface)" }}>
        <table className="w-full text-[13px]" style={{ fontVariantNumeric: "tabular-nums" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--v3-line)" }}>
              {["Bucket", "Bets", "Expected", "Realised", "Δ"].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-[10px] uppercase" style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)", letterSpacing: "0.14em", fontWeight: 500 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(bins).map(([label, b]) => (
              <tr key={label} style={{ borderBottom: "1px solid var(--v3-line)" }} data-testid={`ah-calib-bin-${label}`}>
                <td className="px-3 py-2" style={{ fontFamily: "var(--v3-font-mono)" }}>{label}</td>
                <td className="px-3 py-2">{b.bets}</td>
                <td className="px-3 py-2">{b.expected_pct.toFixed(1)}%</td>
                <td className="px-3 py-2">{b.hit_rate_pct.toFixed(1)}%</td>
                <td className="px-3 py-2" style={{ color: Math.abs(b.calibration_error_pct) <= 5 ? "var(--v3-indigo-fg)" : "var(--v3-rose, #fda4af)" }}>
                  {b.calibration_error_pct >= 0 ? "+" : ""}{b.calibration_error_pct.toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </V3Section>
  );
}


// ── Small bits ────────────────────────────────────────────────────────

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[10px] uppercase" style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)", letterSpacing: "0.14em", fontWeight: 500 }}>
        {label}
      </span>
      <input
        type={type}
        step={type === "number" ? "0.01" : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="px-2.5 py-2 text-[13px] bg-transparent focus:outline-none transition-colors"
        style={{
          border: "1px solid var(--v3-line-2)",
          color: "var(--v3-text)",
          fontFamily: "var(--v3-font-mono)",
          fontVariantNumeric: "tabular-nums",
        }}
      />
    </label>
  );
}

function MonoInput({ value, onChange, placeholder, width = 100, testId, type = "text" }) {
  return (
    <input
      type={type}
      step={type === "number" ? "0.01" : undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="px-2 py-1.5 text-[12px] bg-transparent focus:outline-none"
      style={{
        border: "1px solid var(--v3-line-2)",
        color: "var(--v3-text)",
        fontFamily: "var(--v3-font-mono)",
        width,
      }}
    />
  );
}

function Bar({ label, pct }) {
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <div className="w-16 text-right" style={{ color: "var(--v3-text-3)" }}>{label}</div>
      <div className="flex-1 h-1" style={{ background: "var(--v3-line)" }}>
        <div className="h-full" style={{ width: `${Math.min(100, pct)}%`, background: "var(--v3-indigo)" }} />
      </div>
      <div className="w-12 text-right" style={{ fontFamily: "var(--v3-font-mono)", fontVariantNumeric: "tabular-nums" }}>
        {pct.toFixed(1)}%
      </div>
    </div>
  );
}

function KVList({ rows }) {
  return (
    <div className="mt-4 text-[12px] space-y-1.5">
      {rows.map(([k, v, testid]) => (
        <div key={k} className="flex items-center justify-between gap-3" data-testid={testid}>
          <span style={{ color: "var(--v3-text-3)" }}>{k}</span>
          <span style={{ fontWeight: 500 }}>{v}</span>
        </div>
      ))}
    </div>
  );
}
